import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Paper,
    Typography,
    Box
} from '@mui/material';
import { toast } from "react-toastify";
import { Icon } from "@iconify/react";
import plusIcon from '@iconify/icons-mdi/eye-circle-outline';
import '../../../styles/Auth/sales.css';
import { getData } from '../../../webService/webservice.js';
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import ViewInvoicePopup from "./invoicePopup.jsx";
const SalesPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const sessionToken = sessionStorage.getItem("sessionToken");
    const [limit, setLimit] = useState(5);
    const [invoices, setInvoices] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [refreshKey, setRefreshKey] = useState(0);
    const [viewInvoiceData, setViewInvoiceData] = useState(null);
    const [openViewPopup, setOpenViewPopup] = useState(false);
    const [filters, setFilters] = useState({
        startDate: "",
        endDate: ""
    });

    const commonCellStyle = {
        color: '#525252',
        fontSize: '12px',
        fontWeight: 600,
        textAlign: 'center'
    };
    const headings = [
        'SL',
        'Date',
        'Invoice No',
        'Customer',
        'Total Items',
        'Total Quantity',
        'Total Discount',
        'Total Price',
        'Payment Method',
        'Action'
    ];
    const handlePageChange = (direction) => {
        setCurrentPage((prev) =>
            direction === "next" ? Math.min(prev + 1, totalPages) : Math.max(prev - 1, 1)
        );
    };
    const handleItemsPerPageChange = (e) => {
        const value = e.target.value;
        const newLimit = value === "all" ? "all" : Number(value);
        setLimit(newLimit);
        setCurrentPage(1);
    };
    const fetchInvoices = async (page, limit, searchQuery, filters = {}) => {
        try {
            setLoading(true);

            const params = {
                page,
                limit,
                title: searchQuery?.trim() || ""
            };
            if (filters.startDate) params.startDate = filters.startDate;
            if (filters.endDate) params.endDate = filters.endDate;


            const response = await getData('invoices/invoices/', params, sessionToken);

            // ✅ Correctly unwrap API structure
            const wrapper = response?.data?.[0] || {};

            const paginatedResults = wrapper?.paginatedResults || [];
            const totalCount = wrapper?.totalCount || 0;
            const totalPages = wrapper?.totalPages || 1;
            const currentPage = wrapper?.currentPage || 1;

            // ✅ Set states properly
            setInvoices(paginatedResults);
            setTotalCount(totalCount);
            setTotalPages(totalPages);
            setCurrentPage(currentPage);

        } catch (error) {
            toast.error(error.message || "Failed to fetch Invoices");
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        const token = sessionStorage.getItem("sessionToken");
        fetchInvoices(currentPage, limit, searchQuery, token, filters);
    }, [currentPage, limit, refreshKey, searchQuery, filters]);

    const triggerRefresh = () => {
        setRefreshKey(prev => prev + 1);
    };

    const downloadInvoicePDF = async (invoice) => {
        try {

            const container = document.createElement("div");
            container.style.position = "absolute";
            container.style.left = "-9999px"; // hide offscreen
            container.innerHTML = generateInvoiceHTML(invoice); // see below
            document.body.appendChild(container);

            const canvas = await html2canvas(container, { scale: 2 });
            const imgData = canvas.toDataURL("image/png");

            const pdf = new jsPDF("p", "mm", "a4");
            const imgProps = pdf.getImageProperties(imgData);
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

            pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
            pdf.save(`Invoice-${invoice.invoiceNo}.pdf`);

            document.body.removeChild(container);
        } catch (err) {
            console.error("PDF generation error:", err);
        }
    };

    const generateInvoiceHTML = (invoice) => `
  <div style="max-width:800px; font-family: Arial, sans-serif; font-size:12px; border:2px solid #000; padding:20px; background:#fff;">
    <p style="text-align:center; margin:5px 0;">Invoice Receipt</p>
    <hr style="border:1px dashed #000; margin:10px 0;"/>

    <!-- Header details -->
    <table style="width:100%; margin-bottom:10px;">
      <tr>
        <td><strong>Invoice No:</strong> ${invoice.invoiceNo}</td>
        <td style="text-align:right"><strong>Customer:</strong> ${invoice.customerId?.name || "Walk-in"}</td>
        <td style="text-align:right"><strong>Biller:</strong> ${invoice.billBy?.name || "Walk-in"}</td>
      </tr>
      <tr>
        <td><strong>Date:</strong> ${new Date(invoice.createdAt).toLocaleString()}</td>
        <td style="text-align:right"><strong>Payment Mode:</strong> ${invoice.paymentMode}</td>
      </tr>
    </table>
    <hr style="border:1px dashed #000; margin:10px 0;"/>

    <!-- Items -->
    <table style="width:100%; border-collapse: collapse;">
      <thead>
        <tr>
          <th style="border:1px solid #000; padding:5px; text-align:left;">Item</th>
          <th style="border:1px solid #000; padding:5px; text-align:right;">Qty</th>
          <th style="border:1px solid #000; padding:5px; text-align:right;">Price</th>
          <th style="border:1px solid #000; padding:5px; text-align:right;">Discount Type</th>
          <th style="border:1px solid #000; padding:5px; text-align:right;">Discount Value</th>
          <th style="border:1px solid #000; padding:5px; text-align:right;">Total</th>
        </tr>
      </thead>
      <tbody>
        ${invoice.items.map(item => `
          <tr>
            <td style="border:1px solid #000; padding:5px; text-align:left;">${item.productName || item.serviceName}</td>
            <td style="border:1px solid #000; padding:5px; text-align:right;">${item.qty}</td>
            <td style="border:1px solid #000; padding:5px; text-align:right;">₹${(item.price ?? 0).toFixed(2)}</td>
            <td style="border:1px solid #000; padding:5px; text-align:right;">${item.discountType || '-'}</td>
            <td style="border:1px solid #000; padding:5px; text-align:right;">₹${(item.discountValue ?? 0).toFixed(2)}</td>
            <td style="border:1px solid #000; padding:5px; text-align:right;">₹${(item.total ?? 0).toFixed(2)}</td>
          </tr>
        `).join("")}
      </tbody>
    </table>

    <!-- Summary -->
    <table style="width:100%; margin-top:10px;">
      <tr>
        <td><strong>Total Products:</strong></td>
        <td style="text-align:right;">${invoice.totalProductQty} (₹${(invoice.totalProductAmount ?? 0).toFixed(2)})</td>
      </tr>
      <tr>
        <td><strong>Total Services:</strong></td>
        <td style="text-align:right;">${invoice.totalServiceQty} (₹${(invoice.totalServiceAmount ?? 0).toFixed(2)})</td>
      </tr>
      <tr>
        <td><strong>Subtotal:</strong></td>
        <td style="text-align:right;">₹${(invoice.totalAmount ?? 0).toFixed(2)}</td>
      </tr>
      <tr>
        <td><strong>Discount:</strong></td>
        <td style="text-align:right;">-₹${(invoice.discount ?? 0).toFixed(2)}</td>
      </tr>
      ${invoice.couponCode ? `<tr><td><strong>Coupon (${invoice.couponCode}):</strong></td><td style="text-align:right;">-₹${(invoice.couponDiscount ?? 0).toFixed(2)}</td></tr>` : ''}
      <tr>
        <td><strong>SGST (50% of GST):</strong></td>
        <td style="text-align:right;">₹${(invoice.totalGST / 2).toFixed(2)}</td>
      </tr>
      <tr>
        <td><strong>IGST (50% of GST):</strong></td>
        <td style="text-align:right;">₹${(invoice.totalGST / 2).toFixed(2)}</td>
      </tr>
      <tr style="border-top:2px solid #000; font-weight:bold;">
        <td><strong>Total:</strong></td>
        <td style="text-align:right;">₹${(invoice.grandTotal ?? 0).toFixed(2)}</td>
      </tr>
    </table>

    <hr style="border:1px dashed #000; margin:10px 0;"/>
    <p style="text-align:center;">Thank You! Visit Again</p>
  </div>
`;

    const handleViewInvoice = async (invoiceId) => {
        try {
            setLoading(true);

            // Assuming sessionToken is from sessionStorage
            const sessionToken = sessionStorage.getItem("sessionToken");

            // Fetch data using your getData function
            const response = await getData(`invoices/invoice/${invoiceId}`, sessionToken);

            // Check if response is already JSON
            const data = response?.data || response;

            // Set the view data
            setViewInvoiceData(data);
            setOpenViewPopup(true);
        } catch (error) {
            console.error("Error fetching Invoice:", error);
            toast.error(error.message || "Failed to fetch Invoice data");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="sales-layout">
            <div className="sales-content">
                <div className="sales-main">
                    <div className="sales-widgets">
                        <div className="sales-widget-header">
                            <h3>Sales</h3>
                            <input
                                type="text"
                                placeholder="Search"
                                className="Sales-search-input"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            <div className="Stock-filterDate">
                                <div className="Start-Date"
                                    style={{
                                        display: " flex",
                                        padding: "10px",
                                        gap: "10px",
                                        alignItems: "center",
                                    }}
                                >
                                    <label>Start Date</label>
                                    <input
                                        type="date"
                                        id="startDate"
                                        className="stock-date-input"
                                        value={filters.startDate || ""}
                                        onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                                        style={{
                                            background: "#e6cf80",
                                            border: "none",
                                            padding: "10px",
                                            borderRadius: "10px"
                                        }}
                                    />
                                </div>
                                <div className="End-Date">
                                    <label>End Date</label>
                                    <input
                                        type="date"
                                        id="endDate"
                                        className="stock-date-input"
                                        value={filters.endDate || ""}
                                        onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                                        style={{
                                            background: "#e6cf80",
                                            border: "none",
                                            padding: "10px",
                                            borderRadius: "10px"
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="Clear-button">
                                <button type="clear-stock"
                                    style={{
                                        background: "#8d2629",
                                        color: "white",
                                        fontWeight: "600px",
                                        padding: "10px",
                                        border: "1px solid #e6cf80",
                                        borderRadius: "10px"
                                    }}
                                    onClick={() => setFilters("")}
                                >
                                    Clear
                                </button>
                            </div>
                            <div className="Export-button">
                                <button type="export-stock"
                                    style={{
                                        background: "#8d2629",
                                        color: "white",
                                        fontWeight: "600px",
                                        padding: "10px",
                                        border: "1px solid #e6cf80",
                                        borderRadius: "10px"
                                    }}
                                >
                                    Export
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="sales-divider"></div>
                    <TableContainer component={Paper} sx={{
                        borderRadius: '12px',
                        maxHeight: 400,   // fixed height for table container
                        overflowY: "auto",
                        '&::-webkit-scrollbar': { display: 'none' },
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                    }}>
                        <Table className="custom-product-table"
                            sx={{
                                '& td, & th': {
                                    border: 'none',
                                },
                                border: 'none',
                            }}>
                            <TableHead className="custom-product-head">
                                <TableRow sx={{ backgroundColor: '#F7EAE2' }}>
                                    {headings.map((heading, index) => (
                                        <TableCell
                                            key={index}
                                            sx={{
                                                border: 'none',
                                                padding: '12px 16px',
                                                textAlign: 'center',
                                                position: "sticky",
                                                top: 0,
                                                backgroundColor: '#e6cf80',
                                                zIndex: 1,

                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '4px',
                                                    color: '#000000',
                                                    fontSize: '14px',
                                                    fontWeight: 600,
                                                }}
                                            >
                                                {heading}
                                            </Typography>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {!invoices || invoices.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8}>
                                            <Typography
                                                sx={{
                                                    textAlign: 'center',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    padding: '20px',
                                                    fontWeight: 500
                                                }}
                                            >
                                                No Sales found.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    invoices.map((invoice, index) => {
                                        const paidAmount = invoice.payments?.reduce((sum, p) => sum + p.amount, 0) || 0;
                                        const balance = (invoice.totals?.netValue || 0) - paidAmount;

                                        return (
                                            <TableRow key={invoice._id}>
                                                {/* SL */}
                                                <TableCell>
                                                    <Typography sx={commonCellStyle}>
                                                        {limit === "all"
                                                            ? index + 1
                                                            : (currentPage - 1) * Number(limit) + index + 1}
                                                    </Typography>
                                                </TableCell>

                                                {/* Date */}
                                                <TableCell>
                                                    <Typography sx={commonCellStyle}>
                                                        {new Date(invoice.createdAt).toLocaleDateString("en-IN", {
                                                            day: "2-digit",
                                                            month: "short",
                                                            year: "numeric",
                                                        })}
                                                    </Typography>
                                                </TableCell>

                                                {/* Invoice No */}
                                                <TableCell>
                                                    <Typography sx={commonCellStyle}>
                                                        {invoice.invoiceNo || "-"}
                                                    </Typography>
                                                </TableCell>

                                                {/* Customer */}
                                                <TableCell>
                                                    <Typography sx={commonCellStyle}>
                                                        {invoice.customerName || "N/A"}
                                                    </Typography>
                                                </TableCell>

                                                {/* Total Items */}
                                                <TableCell>
                                                    <Typography sx={commonCellStyle}>
                                                        {invoice.totals?.items || 0}
                                                    </Typography>
                                                </TableCell>

                                                {/* Total Quantity */}
                                                <TableCell>
                                                    <Typography sx={commonCellStyle}>
                                                        {invoice.items?.reduce((sum, i) => sum + Number(i.qty || 0), 0) || 0}
                                                    </Typography>
                                                </TableCell>

                                                {/* Total Discount */}
                                                <TableCell>
                                                    <Typography sx={commonCellStyle}>
                                                        ₹{(invoice.totals?.overallDiscAmount || 0).toFixed(2)}
                                                    </Typography>
                                                </TableCell>

                                                {/* Total Price (Net Value) */}
                                                <TableCell>
                                                    <Typography sx={commonCellStyle}>
                                                        ₹{(invoice.totals?.netValue || 0).toLocaleString("en-IN", {
                                                            minimumFractionDigits: 2,
                                                        })}
                                                    </Typography>
                                                </TableCell>

                                                {/* ✅ Payment Methods (Merged) */}
                                                <TableCell>
                                                    <Typography sx={commonCellStyle}>
                                                        {invoice.payments?.length > 0
                                                            ? (() => {
                                                                // 🔹 Merge duplicate methods
                                                                const grouped = {};
                                                                invoice.payments.forEach(p => {
                                                                    const key = p.paymentMethod || p.method || "Unknown";
                                                                    grouped[key] = (grouped[key] || 0) + Number(p.amount || 0);
                                                                });
                                                                return Object.entries(grouped)
                                                                    .map(
                                                                        ([method, amt]) =>
                                                                            `${method}: ₹${amt.toLocaleString("en-IN", {
                                                                                minimumFractionDigits: 2,
                                                                            })}`
                                                                    )
                                                                    .join(", ");
                                                            })()
                                                            : "Pending"}
                                                    </Typography>
                                                </TableCell>

                                                {/* Action Buttons */}
                                                <TableCell>
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            gap: 3,
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                        }}
                                                    >
                                                        <Icon icon={plusIcon} width={20} height={20} onClick={() => handleViewInvoice(invoice._id)} />
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                        {openViewPopup && (
                            <ViewInvoicePopup
                                data={viewInvoiceData}
                                onClose={() => setOpenViewPopup(false)}
                            />
                        )}
                    </TableContainer>
                    <div className="sales-divider"></div>
                    <div className="sales-bottom">
                        <div className="sales-section show-entries">
                            <span className="text-label">Show</span>
                            <select className="entries-select" value={limit}
                                onChange={handleItemsPerPageChange}
                            >
                                <option value="5">5</option>
                                <option value="10">10</option>
                                <option value="25">25</option>
                                <option value="all">All</option>
                            </select>
                            <span className="text-label">Entries</span>
                        </div>
                        <div className="sales-section showing-range">
                            {
                                limit === 'all'
                                    ? `Showing all ${totalCount} entries`
                                    : `Showing ${(currentPage - 1) * limit + 1} to ${Math.min(currentPage * limit, totalCount)} of ${totalCount} entries`
                            }
                        </div>
                        <div className="sales-section spacer"></div>
                        <div className="sales-section pagination-buttons">
                            <button
                                className="pagination-btn"
                                disabled={currentPage === 1}
                                onClick={() => handlePageChange("prev")}
                            >Previous</button>
                            <button
                                className="pagination-btn"
                                disabled={currentPage === totalPages}
                                onClick={() => handlePageChange("next")}
                            >Next</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SalesPage;